import { Grid, Typography, Button, Box, Divider, TextField, FormControl, InputLabel, MenuItem, IconButton } from '@mui/material'
import { ChatBubbleOutlineOutlined, FmdGoodOutlined, Upload, WorkspacePremiumOutlined } from '@mui/icons-material';
import { toPascalCase } from '../../../../../Utils/String';
import { useAppSelector } from '../../../../../Redux/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { useUpdateClientMutation, useUploadClientProfilePictureMutation } from '../../../../../Redux/api/clientApi';
import { KaziAvatar } from '../../../../../Shared Components/Kazi/KaziAvatar';
import { useGetManyJobsQuery } from '../../../../../Redux/api/jobApi';
import moment from 'moment';
import { Client } from '../../../../../Types/Client';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { languages } from '../../../../../Types/General';
import { useToast } from '../../../../../Hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useMobileView } from '../../../../../Hooks/useMobileView';

interface IEditClientProfileProps {
    clientData: Client;
    mode: 'view' | 'edit';
    setMode: Dispatch<SetStateAction<'view' | 'edit'>>;
}

interface IEditClientForm {
    company: string;
    country: string;
    primary_language: string;
    secondary_language: string;
    description: string;
}

export const EditClientProfile = ({clientData, mode, setMode}: IEditClientProfileProps) => {
    /* ******************** Hooks ******************** */
    const { showToast } = useToast();
    const navigate = useNavigate();
    const userId = useAppSelector( state => state.Global.userId);
    const {data: jobData, isLoading: jobDataLoading, isFetching: jobDataFetching} = useGetManyJobsQuery(clientData?.posted_jobs || skipToken); 
    const {
        control,
        handleSubmit,
        formState: {isDirty, errors},
    } = useForm<IEditClientForm>({
        defaultValues: {
            company: clientData?.company ?? "",
            country: clientData?.country ?? "",
            primary_language: clientData?.languages[0] ?? "",
            secondary_language: clientData?.languages[1] ?? "",
            description: clientData?.description ?? ""
        }
    });
    const [uploadPhoto, {data: uploadResponse, isLoading: isUploading}] = useUploadClientProfilePictureMutation();
    const [updateClient, {data: clientUpdateResponse, isLoading: clientUpdateResponseLoading}] = useUpdateClientMutation();
    
    const onDrop = useCallback((acceptedFiles: any ) => {
        setUploadedFile(acceptedFiles[0]);
    }, [])
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({onDrop, accept: {'image/png': ['.png'], 'image/jpg': ['.jpg'],}, maxFiles: 1, maxSize: 10000000});

    /* ******************** Variables ******************** */
    const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined);
    const hasChanges = useMemo(() => {
        return isDirty || typeof uploadedFile != 'undefined'
    }, [isDirty, uploadedFile])
    const { flexDirection, isMobileView } = useMobileView();
    /* ******************** Functions ******************** */
    const formatLanguages = (primary: string, secondary: string) => {
        let languages = [];
        if (primary.length > 0) languages.push(primary);
        if (secondary.length > 0) languages.push(secondary);
        return languages;
    }
    const handleCancel = () => {
        setMode('view');
        return;
    }
    const handleSave = (data: IEditClientForm) => {   
        if (uploadedFile) {
            let photoFormData = new FormData();
            photoFormData.append('file', uploadedFile);
            uploadPhoto({locator: userId!, body: photoFormData})
            .unwrap()
            .catch(err => {
                console.error("Error occurred while uploading profile picture", err)
                showToast({
                    text: "Unable to upload profile picture",
                    variant: "error"
                })            
            });
        }      
        updateClient({
            locator: userId,
            updated_data: {
                description: data.description,
                company: data.company,
                country: data.country,
                languages: formatLanguages(data.primary_language, data.secondary_language)
            }
        })
        .unwrap()
        .then(res =>
            setMode('view')
        )
        .catch(err => {
            console.error("Error occurred while updating client", err)
            showToast({
                text: "Unable to update client",
                variant: 'error'
            })
        });
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid container display={"flex"} flexDirection={flexDirection} sx={{color: 'primary.main'}} columnGap={15}>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={"flex"} flexDirection={"row"} gap={4}>      
                    <Grid display={"flex" } flexDirection='column' justifyContent={'center'} gap={4}>
                            <Grid position={'relative'}>          
                                <KaziAvatar 
                                    locator={clientData?.locator ?? ''} 
                                    first_name={clientData?.first_name || clientData?.company || 'N/A'} 
                                    last_name={clientData?.last_name ?? ''} 
                                    src={uploadedFile && URL.createObjectURL(uploadedFile)}
                                    sx={{alignSelf: 'center', fontSize: 50, opacity: 0.5}} 
                                />
                                <IconButton {...getRootProps()} sx={{position: 'absolute', top: 42, right: 42, backgroundColor: '#00000040'}}>
                                    <input {...getInputProps()} />
                                    <Upload color='secondary'/>
                                </IconButton>
                            </Grid>
                            {isMobileView && 
                                <Grid display={'flex'} flexDirection={'column'} gap={1}>
                                    <Button 
                                        variant={'contained'} 
                                        size='small' 
                                        disabled={!hasChanges}
                                        onClick={handleSubmit(handleSave)}
                                    >
                                        Save
                                    </Button>
                                    <Button 
                                        variant={'contained'} 
                                        size='small' 
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            }
                        </Grid>     
                        <Grid item display={"flex"} flexDirection={"column"} gap={'16px'} maxWidth={isMobileView ? 250 : '100%'}>
                            <Typography variant='lora-headline-small' fontWeight={"bold"}>
                                {`${toPascalCase(clientData?.first_name || clientData?.company || "") } ${clientData?.last_name ? clientData?.last_name?.substring(0,1).toUpperCase() + '.' : ''}`}
                            </Typography>
                            <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                                <WorkspacePremiumOutlined />
                                <Controller 
                                    name="company"
                                    control={control}
                                    rules={{required: false}}
                                    render={({ field }) =>(                     
                                        <TextField
                                            {...field}
                                            // error={hasErrors(field.name, errors)}
                                            // helperText={hasErrors(field.name, errors)?"Required":""}
                                            variant='standard'
                                            placeholder="Company"
                                            sx={{
                                                width: '100%'
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                                <FmdGoodOutlined />
                                <Controller 
                                    name="country"
                                    control={control}
                                    rules={{required: true}}
                                    render={({ field }) =>(                     
                                        <TextField
                                            {...field}
                                            // error={hasErrors(field.name, errors)}
                                            // helperText={hasErrors(field.name, errors)?"Required":""}
                                            variant='standard'
                                            placeholder="Country"
                                            sx={{
                                                width: '100%'
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                                <ChatBubbleOutlineOutlined />
                                <Grid item display={"flex"} flexDirection={flexDirection} gap={1} maxWidth={isMobileView ? 200 : '100%'}>
                                    <Controller 
                                        name="primary_language"
                                        control={control}
                                        rules={{required: false}}
                                        render={({ field }) =>(
                                            <FormControl>   
                                                <InputLabel sx={{opacity: 1, margin: -1.5}}>{field.value ?"":"Primary language"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    variant='standard'
                                                    sx={{
                                                        width: '200px'
                                                    }}
                                                >
                                                    {
                                                        languages.map((language) => (
                                                            <MenuItem key={language} value={language}>
                                                                {language}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>    
                                        )}
                                    />
                                    <Controller 
                                        name="secondary_language"
                                        control={control}
                                        rules={{required: false}}
                                        render={({ field }) =>(
                                            <FormControl>   
                                                <InputLabel sx={{opacity: 1, margin: -1.5}}>{field.value ?"":"Secondary language (optional)"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    variant='standard'
                                                    sx={{
                                                        width: '200px'
                                                    }}
                                                >
                                                    {
                                                        languages.map((language) => (
                                                            <MenuItem key={language} value={language}>
                                                                {language}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>    
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>About</Typography>
                        <Controller
                            name="description"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>(            
                                <TextField
                                    {...field}
                                    placeholder='Add a short description about yourself'
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    // error={hasErrors(field.name, errors)}
                                    sx={{
                                        width: "100%",
                                        '& .MuiInputBase-input': {
                                        maxHeight: '126px'
                                        }
                                    }}
                                />
                            
                            )}
                        />  
                    </Grid>
                </Grid>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={'flex'} flexDirection={'row'} marginBottom={isMobileView ?  0 : 18} justifyContent={'flex-end'} width={'50%'}>    
                        <Grid item display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} marginBottom={18}>
                            <Grid display={'flex'} flexDirection={'row'}>
                                {!isMobileView &&
                                    <Grid display={'flex'} flexDirection={'row'} gap={1}>
                                        <Button 
                                            variant={'contained'} 
                                            size='large' 
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant={'contained'} 
                                            size='large' 
                                            disabled={!hasChanges}
                                            onClick={handleSubmit(handleSave)}
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                }
                            </Grid > 
                        </Grid> 
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}